import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Download, ExternalLink, FileBarChart2, Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import { api } from "@/api/client";
import type { ReportDetail, ReportStatus } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { ScoreBar } from "@/components/ui/score-bar";
import { Spinner } from "@/components/ui/spinner";
import { averageScore, formatModelLabel } from "@/lib/format";
import { METHODOLOGY_DISCLOSURE } from "../../../methodology";

type LoadState = "loading" | "ready" | "not-found" | "error";

const terminalStatuses: ReportStatus[] = ["completed", "failed"];

function formatDate(value: string | null) {
  if (!value) return "Not yet";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const shortModel = formatModelLabel;

function meanScore(scores: Record<string, number>) {
  return averageScore(scores) ?? 0;
}

function statusBadge(status: ReportStatus) {
  if (status === "running") {
    return <StatusBadge status={status} className="gap-1" label={<><Loader2 className="h-3 w-3 animate-spin" /> Generating</>} />;
  }
  if (status === "failed") return <StatusBadge status={status} label="Failed" />;
  return <StatusBadge status={status} label="Completed" />;
}

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function ReportResultsPage() {
  const { reportId } = useParams();
  const parsedId = Number(reportId);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [error, setError] = useState("");

  const fetchReport = useCallback(async () => {
    if (!Number.isInteger(parsedId) || parsedId <= 0) {
      setLoadState("not-found");
      return;
    }

    try {
      const next = await api.reports.get(parsedId);
      setDetail(next);
      setLoadState("ready");
      setError("");
    } catch (err: any) {
      const message = err?.message || "Unable to load report";
      setError(message);
      setLoadState(message.toLowerCase().includes("not found") ? "not-found" : "error");
    }
  }, [parsedId]);

  useEffect(() => {
    setLoadState("loading");
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (!detail || terminalStatuses.includes(detail.report.status)) return;
    const timer = window.setInterval(fetchReport, 4000);
    return () => window.clearInterval(timer);
  }, [detail, fetchReport]);

  const dimensions = useMemo(() => {
    const names = new Set<string>();
    detail?.aggregates.forEach(aggregate => {
      Object.keys(aggregate.avg_scores).forEach(dim => names.add(dim));
    });
    return Array.from(names);
  }, [detail]);

  const aggregatesWithScores = useMemo(() => {
    return (detail?.aggregates || []).filter(aggregate =>
      aggregate.total_runs > 0 || aggregate.wins > 0 || Object.keys(aggregate.avg_scores).length > 0
    );
  }, [detail]);

  const modelBreakdownsWithRuns = useMemo(() => {
    return (detail?.by_model || []).filter(model =>
      model.total_runs > 0 || model.completed_runs > 0 || Object.keys(model.brand_scores).length > 0
    );
  }, [detail]);

  if (loadState === "loading") {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-slate-500">Loading report results...</p>
        </div>
      </div>
    );
  }

  if (loadState === "not-found") {
    return (
      <EmptyState
        icon={FileBarChart2}
        title="Report not found"
        description="This report may have been deleted or belongs to a different workspace."
      />
    );
  }

  if (loadState === "error" || !detail) {
    return (
      <Card className="border-red-100 bg-red-50">
        <CardContent className="flex items-start gap-3">
          <TriangleAlert className="mt-0.5 h-5 w-5 text-red-600" />
          <div>
            <h2 className="text-sm font-semibold text-red-900">Could not load report</h2>
            <p className="mt-1 text-sm text-red-700">{error || "Try refreshing the page."}</p>
            <Button className="mt-4" variant="secondary" size="sm" onClick={fetchReport}>Try again</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { report } = detail;
  const shareUrl = `/reports/${report.slug}`;
  const isRunning = report.status === "running";
  const isCompleted = report.status === "completed";
  const hasCompletedData = detail.completed_runs > 0 && (aggregatesWithScores.length > 0 || detail.raw_runs.some(run => run.status === "completed" && run.results.length > 0));
  const hasAnyRunData = detail.raw_runs.length > 0 || modelBreakdownsWithRuns.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="secondary" size="sm" asChild>
          <Link to="/app/reports"><ArrowLeft className="mr-1.5 h-4 w-4" />Back to reports</Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" asChild>
            <a href={shareUrl} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" />Public report</a>
          </Button>
          <Button variant="secondary" size="sm" asChild>
            <Link
              to="/app/reports"
              state={{
                reportTemplate: {
                  name: `Copy of ${report.name}`,
                  sourceName: report.name,
                  brand_ids: report.brand_ids,
                  prompt_id: report.prompt_id,
                  models: report.models,
                  runs_per_model: report.runs_per_model,
                  query_mode: report.query_mode,
                },
              }}
            >
              <Copy className="mr-1.5 h-4 w-4" />Use as template
            </Link>
          </Button>
          {isCompleted && (
            <Button variant="secondary" size="sm" asChild>
              <a href={`${shareUrl}/export.docx`}><Download className="mr-1.5 h-4 w-4" />Export .docx</a>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {statusBadge(report.status)}
                {report.query_mode === "blind_query" && <Badge>Blind Query</Badge>}
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-brand-950">{report.name}</h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-500">
                {detail.prompt_name} · {Object.values(detail.brand_names).join(", ")} · {report.models.length} model{report.models.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-right">
              <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Runs</p><p className="text-lg font-semibold text-brand-950">{detail.completed_runs}/{detail.total_runs}</p></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Failed</p><p className="text-lg font-semibold text-brand-950">{detail.failed_runs}</p></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Completed</p><p className="text-sm font-semibold text-brand-950">{formatDate(report.completed_at)}</p></div>
            </div>
          </div>

          {isRunning && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-center gap-2 font-semibold"><RefreshCw className="h-4 w-4 animate-spin" />Report is generating</div>
              <p className="mt-1 text-[13px] leading-relaxed">This page refreshes automatically while the model runs and synthesis complete.</p>
            </div>
          )}

          {report.status === "failed" && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <p className="font-semibold">Report generation failed.</p>
              <p className="mt-1 text-[13px] leading-relaxed">Some runs may still be visible below if they completed before the failure.</p>
            </div>
          )}

          <p className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-[12px] leading-relaxed text-slate-500">
            {METHODOLOGY_DISCLOSURE}
          </p>
        </CardContent>
      </Card>

      {isCompleted && !hasCompletedData ? (
        <EmptyState
          icon={FileBarChart2}
          title="No report data available"
          description="The report completed, but no completed runs or aggregate data were returned."
        />
      ) : (
        <>
          {report.exec_summary && (
            <Section title="Executive summary">
              <p className="whitespace-pre-line text-sm leading-7 text-slate-700">{report.exec_summary}</p>
            </Section>
          )}

          {!isCompleted && !hasCompletedData && !hasAnyRunData && (
            <EmptyState
              icon={FileBarChart2}
              title={isRunning ? "No completed runs yet" : "No completed report data available"}
              description={isRunning
                ? "Results will appear here as soon as the first model run completes."
                : "The report failed before any model runs returned usable results."}
            />
          )}

          {aggregatesWithScores.length > 0 && (
            <Section title="Brand leaderboard" description="Average scores and wins across completed runs.">
              <div className="space-y-4">
                {aggregatesWithScores.map((aggregate, index) => (
                  <div key={aggregate.brand_id} className="rounded-lg border border-slate-100 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Badge variant={index === 0 ? "winner" : "muted"}>#{index + 1}</Badge>
                        <h3 className="font-semibold text-brand-950">{aggregate.brand_name}</h3>
                      </div>
                      <div className="text-right text-[12px] text-slate-500">{aggregate.wins} wins · {aggregate.win_rate}% win rate · avg {meanScore(aggregate.avg_scores)}</div>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(aggregate.avg_scores).map(([dimension, score]) => (
                        <ScoreBar key={dimension} label={dimension} score={score} isWinner={index === 0} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {report.brand_synthesis && report.brand_synthesis.length > 0 && (
            <Section title="Brand synthesis" description="Narrative takeaways generated from completed report runs.">
              <div className="grid gap-4 md:grid-cols-2">
                {report.brand_synthesis.map(brand => (
                  <article key={`${brand.brand_id}-${brand.brand_name}`} className="rounded-lg border border-slate-100 p-4">
                    <h3 className="font-semibold text-brand-950">{brand.brand_name}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{brand.narrative}</p>
                    {brand.quotes.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {brand.quotes.slice(0, 2).map((quote, index) => (
                          <blockquote key={`${quote.model}-${index}`} className="border-l-2 border-brand-200 pl-3 text-[12px] leading-5 text-slate-500">
                            “{quote.quote}” <span className="font-medium text-slate-600">— {shortModel(quote.model)}</span>
                          </blockquote>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </Section>
          )}

          {modelBreakdownsWithRuns.length > 0 && (
            <Section title="Model breakdown" description="Completed runs, winners, and average brand scores by model.">
              <div className="grid gap-4 md:grid-cols-2">
                {modelBreakdownsWithRuns.map(model => (
                  <div key={model.model} className="rounded-lg border border-slate-100 p-4">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-brand-950">{shortModel(model.model)}</h3>
                      <Badge variant="muted">{model.completed_runs}/{model.total_runs} complete</Badge>
                    </div>
                    {Object.keys(model.winners).length > 0 && (
                      <p className="mb-3 text-[12px] text-slate-500">
                        Winners: {Object.entries(model.winners).map(([winner, count]) => `${winner} (${count})`).join(", ")}
                      </p>
                    )}
                    <div className="space-y-3">
                      {Object.entries(model.brand_scores).map(([brandName, scores]) => (
                        <div key={brandName}>
                          <div className="mb-1 flex items-center justify-between text-[12px]"><span className="font-medium text-slate-700">{brandName}</span><span className="text-slate-400">avg {meanScore(scores)}</span></div>
                          <div className="grid gap-1">
                            {dimensions.slice(0, 3).map(dim => <ScoreBar key={dim} label={dim} score={scores[dim] ?? null} size="sm" />)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {detail.mention_aggregates.length > 0 && (
            <Section title="Mentions" description="Tracked mention volume, sentiment, and visibility from available analysis.">
              <div className="space-y-3">
                {detail.mention_aggregates.map(mention => (
                  <div key={mention.brand_name} className="rounded-lg border border-slate-100 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="font-semibold text-brand-950">{mention.brand_name}</h3>
                      <div className="flex gap-2"><Badge variant="muted">{mention.mention_count} mentions</Badge><Badge variant="muted">sentiment {mention.avg_sentiment ?? "N/A"}</Badge><Badge variant="muted">visibility {mention.avg_visibility ?? "N/A"}</Badge></div>
                    </div>
                    {mention.contexts.length > 0 && <p className="mt-2 text-[13px] leading-6 text-slate-500">“{mention.contexts[0]?.context}”</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {detail.raw_runs.length > 0 && (
            <Section title="Run appendix" description="Raw completed and failed runs that power this report.">
              <div className="space-y-3">
                {detail.raw_runs.map(run => (
                  <details key={`${run.model}-${run.run_index}-${run.comparison_id}`} className="rounded-lg border border-slate-100 p-4">
                    <summary className="cursor-pointer list-none text-sm font-semibold text-brand-950">
                      {shortModel(run.model)} · Run {run.run_index + 1} · {run.status}{run.winner ? ` · Winner: ${run.winner}` : ""}
                    </summary>
                    {run.summary && <p className="mt-3 text-sm leading-6 text-slate-600">{run.summary}</p>}
                    {run.analysis_error && <p className="mt-3 text-sm text-red-600">{run.analysis_error}</p>}
                    {run.results.length > 0 && (
                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {run.results.map(result => (
                          <div key={`${run.comparison_id}-${result.brand_id}`} className="rounded-md bg-slate-50 p-3">
                            <p className="text-[13px] font-semibold text-brand-950">{result.brand_name}</p>
                            <p className="mt-1 text-[12px] text-slate-500">{Object.entries(result.scores).map(([dim, score]) => `${dim}: ${score ?? "N/A"}`).join(" · ")}</p>
                            {result.reasoning && <p className="mt-2 text-[12px] leading-5 text-slate-500">{result.reasoning}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </details>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
